import { useState, type ReactNode } from 'react'
import { CircleHelp } from 'lucide-react'
import { Drawer } from './Drawer'

/**
 * "How this works", top right of the screens whose answer lives in Airtable.
 *
 * Written for whoever keeps the base, in the words they use at the counter.
 * The one thing it must get across that nobody guesses: the website is built
 * from Airtable, in a fixed order — Airtable, then Sync gowns, then Publish —
 * and nothing on the website is typed into Shopify. Everything else here is
 * a table of which column becomes which line on the page.
 *
 * The same content backs both the Catalog and the Publish screens, because
 * the question a person asks on either one is the same: "where do I change
 * this?" The developer-facing version, with the fold tables and the field
 * keys, is `docs/19-storefront-fields.md`.
 */

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="st-display mb-2 text-base">{title}</h3>
      {children}
    </section>
  )
}

function FieldTable({ rows }: { readonly rows: ReadonlyArray<readonly [string, string, string]> }) {
  return (
    <div className="st-card overflow-x-auto">
      <table className="st-table text-sm">
        <thead>
          <tr>
            <th>On the website</th>
            <th>Airtable column</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([where, column, note]) => (
            <tr key={where}>
              <td className="font-medium">{where}</td>
              <td className="font-mono text-xs">{column}</td>
              <td className="st-meta">{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function StorefrontHelp() {
  return (
    <>
      <Section title="The order things happen in">
        <ol className="list-decimal space-y-1 pl-5 text-sm">
          <li>
            Change the row in <strong>Airtable</strong> (the MTO table).
          </li>
          <li>
            <strong>Sync gowns</strong>, the owner&rsquo;s button on the Catalog page — scan, read what would change, apply.
            That brings Airtable into this catalog.
          </li>
          <li>
            <strong>Publish</strong> — plan, read what would change on the website, publish. That puts the catalog on the
            website.
          </li>
        </ol>
        <p className="st-meta mt-2">
          Nothing on the website is typed into Shopify. If a page is wrong, the fix is in Airtable, then the two
          buttons. Editing the page in Shopify by hand is undone by the next Publish.
        </p>
      </Section>

      <Section title="Photos">
        <p className="mb-2 text-sm">
          The <span className="font-mono text-xs">Picture with Logo</span> attachments, in the order they are in Airtable:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>
            <strong>1st photo</strong> — the card on the browse page, the large photo on the gown&apos;s page, and what
            Google shows. Front view, full length.
          </li>
          <li>
            <strong>2nd photo</strong> — what the card shows when a customer hovers over it. A different angle: back,
            detail or side.
          </li>
          <li>
            <strong>The rest</strong> — the gallery down the gown&apos;s page, in order.
          </li>
        </ul>
        <p className="st-meta mt-2">
          Drag attachments in Airtable to reorder them. A gown with one photo simply has no hover.
        </p>
      </Section>

      <Section title="What each line comes from">
        <FieldTable
          rows={[
            ['Heading / browser tab / Google title', 'PBF No.', 'The style number is the name. No name is invented.'],
            ['Small caps line above the heading', 'Collection', ''],
            ['Paragraph', 'Long Description', 'Line breaks kept. The first 160 characters are also the Google snippet.'],
            ['Silhouette', 'Silhouette', 'One of A-line, Fit & Flare, Ballgown, Mermaid, Sheath. Anything else stops the sync and names the row.'],
            ['Neckline', 'Neckline', 'Folded to seven: Sweetheart, V-neck, Straight, Scoop, Square, High neck, Asymmetric. "Plunging V" becomes V-neck, "Semi-sweetheart" Sweetheart, "Halter" High neck.'],
            ['Details', 'Detail', 'Comma-separated: Clean (shown as Minimal), Detachable Train, Bow, Basque Waist.'],
            ['Filters on the browse page', '—', 'Built from the silhouettes, necklines and details actually on published gowns. A value nobody uses disappears by itself.'],
          ]}
        />
        <p className="st-meta mt-2">
          Not shown, on purpose: <span className="font-mono text-xs">PBF Retail Price</span> (prices are hidden from
          customers), <span className="font-mono text-xs">Sample Color Detail</span> (the sample&apos;s color, not the
          gown&apos;s).
        </p>
      </Section>

      <Section title="Which gowns are on the website">
        <p className="text-sm">
          Every row in the MTO table with a <span className="font-mono text-xs">PBF No.</span> whose status here is
          active. A gown that disappears from Airtable is marked discontinued by Sync gowns and, on the next Publish,
          taken off the website — it keeps its address, so putting it back later puts it back at the same one.
        </p>
        <p className="st-meta mt-2">
          A row is refused, and named, when its PBF No. is blank, appears twice, or cannot become a web address.
        </p>
      </Section>

      <Section title="What needs a developer">
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>A new silhouette, neckline or detail wording that the sync does not recognize.</li>
          <li>A new line on the page (say, fabric).</li>
          <li>How the page looks — or open Theme in Settings, which lets an AI assistant do it on a draft.</li>
        </ul>
      </Section>
    </>
  )
}

/** The button, and the drawer it opens. Sits in the page header's control slot. */
export function HelpButton({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="st-btn-quiet" onClick={() => setOpen(true)}>
        <CircleHelp size={14} aria-hidden />
        How this works
      </button>
      <Drawer open={open} onOpenChange={setOpen} title={title} eyebrow="How this works">
        {children}
      </Drawer>
    </>
  )
}
